import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, Phone, Globe, Music, Users, Clock, Plane, FileText, ArrowLeft, ExternalLink, Instagram, Play, MapPin } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl } from "../lib/api";
import { ListingHeroImg } from "../lib/termImages";
import { fmtDate, fmtTime } from "../components/Cards";
import { Reveal, SectionTitle, useSeo } from "../components/VIP";
import { ListingActions } from "../components/ListingActions";
import { NominateButton, AwardBadges, VoteCard } from "../components/AwardsWidgets";
import { ReviewsSection } from "../components/Reviews";
import { Paged } from "../components/PagedList";
import { BookingDialog } from "../components/BookingDialog";
import { Sponsors } from "../components/Marketplace";
import { VideoGrid } from "../components/VideoGrid";
import { MerchCard } from "../components/VenueExtras";
import LoyaltyJoinCard from "../components/LoyaltyJoinCard";

export default function ArtistDetail() {
  const { slug } = useParams();
  const [a, setA] = useState(null);
  const [showStickyCta, setShowStickyCta] = useState(false);
  useEffect(() => { api.get(`/artists/${slug}`).then((r) => setA(r.data)).catch(() => setA(false)); window.scrollTo(0, 0); }, [slug]);
  useEffect(() => {
    const onScroll = () => setShowStickyCta(window.scrollY > 600);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useSeo(a ? a.seo_title || `${a.name} | Gulf Coast VIP` : null, a?.bio);

  const upcomingAvailability = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    return ((a && a.availability) || [])
      .map((s) => ({ ...s, _start: s.start_date || s.date || "", _end: s.end_date || s.start_date || s.date || "" }))
      .filter((s) => s._end >= today)
      .sort((x, y) => x._start.localeCompare(y._start));
  }, [a]);

  if (a === false) return <div className="py-40 text-center text-zinc-500">Artist not found.</div>;
  if (!a) return <div className="py-40 text-center text-zinc-500">Loading...</div>;

  const detail = (Icon, label, val) => {
    if (!val) return null;
    const l = label.toLowerCase();
    let href = null;
    if (l.includes("email")) href = `mailto:${val}`;
    else if (l.includes("phone")) href = `tel:${String(val).replace(/[^+\d]/g, "")}`;
    else if (l.includes("website")) href = /^https?:\/\//i.test(val) ? val : `https://${val}`;
    return (
      <div className="flex items-start gap-3" data-testid={`artist-detail-${l.replace(/\s+/g, "-")}`}>
        <Icon size={18} className="text-rose-400 mt-0.5 shrink-0" />
        <div className="min-w-0">
          <p className="text-xs text-zinc-500">{label}</p>
          {href ? (
            <a href={href} target={l.includes("website") ? "_blank" : undefined} rel={l.includes("website") ? "noreferrer" : undefined} className="text-sm text-rose-400 hover:text-rose-300 hover:underline break-words" data-testid={`artist-${l.replace(/\s+/g, "-")}-link`}>{val}</a>
          ) : (
            <p className="text-sm break-words">{val}</p>
          )}
        </div>
      </div>
    );
  };
  const social = a.social_links || {};

  return (
    <div data-testid="artist-detail" className="pb-24 lg:pb-0">
      <div className="relative min-h-[85vh] flex flex-col overflow-hidden grain">
        <ListingHeroImg ent={a} kind="artist" className="absolute inset-0 w-full h-full object-cover hero-zoom" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(244,63,94,0.25),transparent_50%)]" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-black/20" />
        <Link to="/artists" className="relative z-10 self-start mt-6 ml-6 md:ml-12 lg:ml-20 flex items-center gap-1 text-sm text-zinc-300 hover:text-white"><ArrowLeft size={16} /> All artists</Link>
        <div className="relative flex-1 px-6 md:px-12 lg:px-20 flex flex-col justify-end pb-12 pt-10">
          <motion.div initial={{ opacity: 0, y: 36 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }} className="max-w-4xl">
            <span className={`inline-flex items-center gap-2 text-sm px-3 py-1 rounded-full mb-4 ${a.booking_available ? "bg-emerald-500/20 text-emerald-300" : "bg-zinc-500/20 text-zinc-300"}`}>
              <span className={`w-2 h-2 rounded-full ${a.booking_available ? "bg-emerald-400 animate-pulse" : "bg-zinc-400"}`} />
              {a.booking_available ? "Available for booking" : "Contact for availability"}
            </span>
            <p className="overline mb-2 flex items-center gap-1"><Music size={12} /> {(a.genres?.length ? a.genres : [a.genre]).filter(Boolean).join(" · ")}{a.artist_type ? ` · ${a.artist_type}` : ""}</p>
            <div className="flex items-center gap-4">
              {a.logo && <img src={fileUrl(a.logo)} alt="" className="w-16 h-16 md:w-20 md:h-20 rounded-2xl object-cover border border-white/20 shrink-0" data-testid="artist-logo" />}
              <h1 className="font-display font-black text-5xl md:text-8xl leading-[0.9] text-shadow-lg">{a.name}</h1>
              <div className="flex flex-wrap gap-2 mt-3">
                {a.vip && <span className="bg-amber-400 text-black text-xs font-extrabold px-3 py-1 rounded-full" data-testid="artist-vip-badge">VIP</span>}
                {a.priority_gigs && <span className="bg-sky-500/25 text-sky-200 text-xs font-bold px-3 py-1 rounded-full" data-testid="artist-priority-badge">Recommended for Gigs</span>}
                {a.spotlight_badge && <span className="bg-rose-500 text-white text-xs font-extrabold px-3 py-1 rounded-full" data-testid="artist-spotlight-badge">★ {a.spotlight_badge}</span>}
              </div>
            </div>
            {a.award_badges?.length > 0 && <div className="mt-3"><AwardBadges badges={a.award_badges} /></div>}
            <p className="text-lg md:text-xl text-zinc-300 mt-3">{a.tagline} · {a.home_city}, {a.home_state}</p>
            <div className="flex flex-wrap items-center gap-3 mt-6">
              {social.instagram && <a href={social.instagram} target="_blank" rel="noreferrer" className="flex items-center justify-center w-10 h-10 glass rounded-full hover:border-white/30"><Instagram size={16} /></a>}
              {social.spotify && <a href={social.spotify} target="_blank" rel="noreferrer" className="flex items-center justify-center w-10 h-10 glass rounded-full hover:border-white/30"><Music size={16} /></a>}
              <ListingActions entityType="artist" entityId={a.id} claimStatus={a.claim_status || "unclaimed"} verified={!!a.verified} />
              <NominateButton entityType="artist" entity={a} />
            </div>
          </motion.div>
        </div>
      </div>

      <div className="lg:hidden"><VoteCard entityType="artist" entity={a} /></div>

      <LoyaltyJoinCard entityType="artist" entityId={a.id} />

      <div className="px-6 md:px-12 lg:px-20 py-14 grid lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2 space-y-16">
          <Reveal>
            <SectionTitle kicker="The story" title="Biography" />
            <p className="text-zinc-300 leading-relaxed mt-6 text-lg">{a.bio}</p>
          </Reveal>

          {a.band_members?.length > 0 && (
            <Reveal>
              <SectionTitle kicker="Meet the band" title="Band Members" />
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5 mt-6" data-testid="band-members">
                {a.band_members.map((m, i) => (
                  <div key={`${m.name}-${i}`} className="text-center" data-testid={`band-member-${i}`}>
                    <div className="aspect-square rounded-2xl overflow-hidden mb-3">
                      {m.image
                        ? <img src={fileUrl(m.image)} alt={m.name} className="w-full h-full object-cover" />
                        : <div className="w-full h-full bg-white/5 flex items-center justify-center"><Users size={36} className="text-zinc-700" /></div>}
                    </div>
                    <p className="font-semibold">{m.name}</p>
                    <p className="text-xs text-zinc-500">{m.role}</p>
                    {m.bio && <p className="text-xs text-zinc-400 mt-1 line-clamp-3">{m.bio}</p>}
                    {m.social_link && <a href={m.social_link} target="_blank" rel="noreferrer" className="text-xs text-rose-400">Follow</a>}
                  </div>
                ))}
              </div>
            </Reveal>
          )}

          {upcomingAvailability.length > 0 && (
            <Reveal>
              <SectionTitle kicker="Booking calendar" title="Availability" />
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-6" data-testid="availability">
                {upcomingAvailability.map((s, i) => {
                  const tone = { available: "border-emerald-500/40 bg-emerald-500/5", tentative: "border-amber-500/40 bg-amber-500/5", booked: "border-rose-500/40 bg-rose-500/5", unavailable: "border-white/10 bg-white/5" }[s.status] || "border-white/10";
                  const dot = { available: "bg-emerald-400", tentative: "bg-amber-400", booked: "bg-rose-400", unavailable: "bg-zinc-500" }[s.status];
                  const range = s._end && s._end !== s._start ? `${fmtDate(s._start)} – ${fmtDate(s._end)}` : fmtDate(s._start);
                  return (
                    <div key={`${s._start}-${s._end}-${i}`} className={`rounded-xl border p-4 ${tone}`} data-testid={`avail-${i}`}>
                      <div className="flex items-center justify-between gap-2">
                        <p className="font-display font-bold text-sm">{range}</p>
                        <span className="flex items-center gap-1.5 text-xs capitalize shrink-0"><span className={`w-2 h-2 rounded-full ${dot}`} /> {s.status}</span>
                      </div>
                      {s.note && <p className="text-xs text-zinc-400 mt-1">{s.note}</p>}
                      {s.status === "available" && <BookingDialog artistId={a.id} artistName={a.name} available={true} trigger={<button className="mt-3 w-full text-xs bg-rose-500 hover:bg-rose-600 font-bold py-2 rounded-lg" data-testid={`avail-book-${i}`}>Request these dates</button>} />}
                    </div>
                  );
                })}
              </div>
            </Reveal>
          )}

          {a.discography?.length > 0 && (
            <Reveal>
              <SectionTitle kicker="Listen" title="Discography" />
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-6 mt-6">
                {a.discography.map((d, i) => (
                  <a key={i} href={d.streaming_link || (d.streaming_links || {}).spotify || "#"} target="_blank" rel="noreferrer" className="group" data-testid={`disc-${i}`}>
                    <div className="aspect-square rounded-2xl overflow-hidden mb-3 relative">
                      <img src={fileUrl(d.cover_image)} alt={d.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"><Play className="text-white" fill="white" size={36} /></div>
                    </div>
                    <p className="font-semibold truncate">{d.title}</p>
                    <p className="text-xs text-zinc-500">{d.release_type} · {d.release_year}</p>
                  </a>
                ))}
              </div>
            </Reveal>
          )}

          {a.videos?.length > 0 && <Reveal><Paged items={a.videos} perKey="artist_videos_pp" perDefault={6} label="videos" testid="artist-videos" render={(slice) => <VideoGrid items={slice} />} /></Reveal>}

          {((a.merch?.length || 0) + (a.marketplace_merch?.length || 0)) > 0 && (
            <Reveal>
              <SectionTitle kicker="Support the artist" title="Merch" />
              <Paged items={[...(a.marketplace_merch || []), ...(a.merch || [])]} perKey="artist_merch_pp" perDefault={6} label="items" testid="artist-merch" render={(slice) => (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-6 mt-6">
                {slice.map((m, i) => <MerchCard key={m.marketplace_slug || i} m={m} testid={`merch-${i}`} />)}
              </div>
              )} />
            </Reveal>
          )}

          {a.upcoming_events?.length > 0 && (
            <Reveal className="lg:hidden">
              <SectionTitle kicker="Catch them live" title="Upcoming Shows" />
              <Paged items={a.upcoming_events} perKey="artist_shows_pp" perDefault={4} label="shows" testid="artist-shows" render={(slice) => (
              <div className="space-y-3 mt-6">
                {slice.map((e) => (
                  <Link key={e.id} to={`/events/${e.slug}`} className="flex items-center gap-5 bg-[#111] border border-white/10 rounded-2xl p-4 card-hover">
                    <div className="text-center w-16 shrink-0 border-r border-white/10 pr-4"><p className="font-display font-black text-2xl text-rose-400">{fmtDate(e.event_date).split(" ")[2]}</p><p className="text-xs text-zinc-400 uppercase">{fmtDate(e.event_date).split(" ")[1]}</p></div>
                    <div className="flex-1"><p className="font-display font-bold text-lg">{e.title}</p><p className="text-sm text-zinc-400">{e.venue_name}, {e.city}{e.start_time ? ` · ${fmtTime(e.start_time)}` : ""}</p></div>
                    <span className="hidden sm:block text-sm text-rose-400 font-medium">Tickets →</span>
                  </Link>
                ))}
              </div>
              )} />
            </Reveal>
          )}

          {a.past_venues?.length > 0 && (
            <Reveal className="lg:hidden">
              <SectionTitle kicker="Track record" title="Previously played" />
              <Paged items={a.past_venues} perKey="artist_venues_pp" perDefault={10} label="venues" testid="artist-venues" render={(slice) => (
              <div className="flex flex-wrap gap-3 mt-6">
                {slice.map((v) => <Link key={v.id} to={`/venues/${v.slug}`} className="bg-[#111] border border-white/10 rounded-full px-5 py-2.5 text-sm card-hover">{v.name}</Link>)}
              </div>
              )} />
            </Reveal>
          )}

          {a.gallery_images?.length > 0 && (
            <Reveal>
              <SectionTitle kicker="In frame" title="Gallery" />
              <Paged items={a.gallery_images} perKey="artist_gallery_pp" perDefault={12} label="photos" testid="artist-gallery" render={(slice) => (
                <div className="columns-2 sm:columns-3 gap-4 space-y-4 mt-6">{slice.map((g, i) => <img key={i} src={fileUrl(g)} alt="" className="w-full rounded-2xl break-inside-avoid" />)}</div>
              )} />
            </Reveal>
          )}

          {a.sponsors?.length > 0 && <Reveal><Sponsors items={a.sponsors} /></Reveal>}
          <ReviewsSection entityType="artist" entityId={a.id} ownerUserId={a.owner_user_id} />
        </div>

        <aside data-testid="artist-booking-panel" className="space-y-6">
          {/* 1. VIP Spotlight Awards */}
          <div className="hidden lg:block"><VoteCard entityType="artist" entity={a} compact /></div>

          {/* 2. Upcoming Shows — venue-style layout */}
          {a.upcoming_events?.length > 0 && (
            <div className="hidden lg:block bg-[#111] border border-white/10 rounded-2xl p-6" data-testid="artist-side-upcoming">
              <div className="flex items-baseline justify-between mb-4">
                <h3 className="font-display font-bold text-lg">Upcoming Shows</h3>
                <Link to={`/events?artist=${a.slug}`} className="text-xs font-bold text-rose-400 hover:text-rose-300" data-testid="artist-side-upcoming-all">View All</Link>
              </div>
              <Paged items={a.upcoming_events} perKey="artist_side_shows_pp" perDefault={3} label="shows" testid="artist-side-upcoming-paged" render={(slice) => (
                <ul className="space-y-3">
                  {slice.map((e) => {
                    const d = e.event_date ? new Date(`${e.event_date}T00:00:00`) : null;
                    const mon = d ? d.toLocaleString("en-US", { month: "short" }).toUpperCase() : "";
                    const day = d ? String(d.getDate()) : "";
                    const dow = d ? d.toLocaleString("en-US", { weekday: "short" }).toUpperCase() : "";
                    const img = fileUrl(e.profile_image || e.event_image);
                    const t = fmtTime(e.start_time);
                    return (
                      <li key={e.id}>
                        <Link to={`/events/${e.slug}`} className="flex items-stretch gap-3 group bg-white/[0.02] hover:bg-white/[0.05] border border-white/5 hover:border-white/15 rounded-xl p-2.5 transition-colors" data-testid={`artist-side-event-${e.slug}`}>
                          <div className="w-14 shrink-0 flex flex-col items-center justify-center bg-white/[0.03] border border-white/10 rounded-lg py-2">
                            <p className="text-[10px] font-extrabold text-rose-400 leading-none tracking-wider">{mon}</p>
                            <p className="font-display font-extrabold text-2xl leading-none mt-1">{day}</p>
                            {dow && <p className="text-[9px] font-bold text-zinc-500 leading-none mt-1.5 tracking-wider">{dow}</p>}
                          </div>
                          <div className="min-w-0 flex-1 flex flex-col justify-center py-0.5">
                            <p className="font-display font-bold text-[15px] text-white group-hover:text-rose-300 leading-tight line-clamp-1">{e.title}</p>
                            {(e.venue_name || e.city) && <p className="text-xs text-zinc-400 mt-1 truncate">{[e.venue_name, e.city].filter(Boolean).join(", ")}</p>}
                            {t && <p className="text-xs text-zinc-400 mt-0.5">{t}</p>}
                          </div>
                          {img ? (
                            <img src={img} alt="" loading="lazy" className="w-20 aspect-[4/3] rounded-lg object-cover border border-white/10 shrink-0" />
                          ) : (
                            <div className="w-20 aspect-[4/3] rounded-lg bg-rose-500/15 border border-white/10 flex items-center justify-center shrink-0"><Music size={20} className="text-rose-300" /></div>
                          )}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              )} />
              <Link to={`/events?artist=${a.slug}`} className="mt-4 block text-center text-sm font-bold border border-rose-500/40 hover:border-rose-500 hover:bg-rose-500/10 text-rose-300 hover:text-rose-200 rounded-full py-2.5 transition-colors" data-testid="artist-side-upcoming-viewall">View All Shows</Link>
            </div>
          )}

          {/* 3. Previously Played — circular venue avatars */}
          {a.past_venues?.length > 0 && (
            <div className="hidden lg:block bg-[#111] border border-white/10 rounded-2xl p-6" data-testid="artist-side-venues">
              <div className="flex items-baseline justify-between mb-4">
                <h3 className="font-display font-bold text-lg">Previously Played</h3>
                <Link to="/venues" className="text-xs font-bold text-rose-400 hover:text-rose-300 shrink-0 ml-2" data-testid="artist-side-venues-all">View All</Link>
              </div>
              <Paged items={a.past_venues} perKey="artist_side_venues_pp" perDefault={3} label="venues" testid="artist-side-venues-paged" render={(slice) => (
                <div className="grid grid-cols-3 gap-2.5">
                  {slice.map((v) => (
                    <Link key={v.id} to={`/venues/${v.slug}`} className="group text-center" data-testid={`artist-side-venue-${v.slug}`}>
                      {fileUrl(v.profile_image || v.hero_image) ? (
                        <img src={fileUrl(v.profile_image || v.hero_image)} alt="" loading="lazy" className="w-full aspect-square rounded-full object-cover border-2 border-white/10 group-hover:border-rose-500/60 transition-colors" />
                      ) : (
                        <div className="w-full aspect-square rounded-full bg-rose-500/15 border-2 border-white/10 text-rose-300 flex items-center justify-center text-xl font-bold"><MapPin size={20} /></div>
                      )}
                      <p className="text-xs font-semibold mt-2 text-zinc-200 group-hover:text-white leading-tight line-clamp-2">{v.name}</p>
                    </Link>
                  ))}
                </div>
              )} />
            </div>
          )}

          {/* 4. Book this artist */}
          <div className="bg-gradient-to-b from-[#161616] to-[#0d0d0d] border border-white/10 rounded-3xl p-7 space-y-4 glow-ring">
            <h3 className="font-display font-bold text-xl">Book this artist</h3>
            <BookingDialog artistId={a.id} artistName={a.name} available={a.booking_available}
              trigger={<button className="w-full bg-rose-500 hover:bg-rose-600 font-bold py-3.5 rounded-xl transition-colors" data-testid="artist-book-btn">Request Booking</button>} />
            <div className="pt-4 border-t border-white/10 space-y-4">
              {detail(Music, "Genre", (a.genres?.length ? a.genres : [a.genre]).filter(Boolean).join(", "))}
              {detail(Users, "Members", a.members)}
              {detail(Music, "Type", a.performance_type)}
              {detail(Clock, "Set length", a.set_length)}
              {detail(Plane, "Travel", a.travel_availability)}
              {detail(Mail, "Booking email", a.booking_email)}
              {detail(Phone, "Booking phone", a.booking_phone)}
              {detail(Globe, "Website", a.website)}
              {a.epk_link && <a href={a.epk_link} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm text-rose-400 hover:underline"><FileText size={16} /> Press Kit / EPK</a>}
            </div>
          </div>
        </aside>
      </div>

      <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 glass sticky-bar px-5 py-3 flex items-center gap-3" data-testid="artist-mobile-bar">
        <BookingDialog artistId={a.id} artistName={a.name} available={a.booking_available}
          trigger={<button className="flex-1 bg-rose-500 font-bold py-3 rounded-xl text-sm" data-testid="artist-book-mobile">Request Booking</button>} />
      </div>

      {/* Desktop floating "Book this artist" pill — appears after scrolling past hero */}
      <div className={`hidden lg:block fixed bottom-6 right-6 z-40 transition-all duration-300 ${showStickyCta ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 translate-y-4 pointer-events-none"}`} data-testid="artist-sticky-cta">
        <BookingDialog artistId={a.id} artistName={a.name} available={a.booking_available}
          trigger={
            <button className="group flex items-center gap-2.5 bg-rose-500 hover:bg-rose-600 text-white font-bold pl-3 pr-5 py-3 rounded-full shadow-xl shadow-rose-500/30 hover:shadow-rose-500/50 transition-shadow" data-testid="artist-book-sticky">
              <span className="w-8 h-8 rounded-full bg-white/15 flex items-center justify-center group-hover:scale-110 transition-transform"><Music size={16} /></span>
              <span className="text-sm">Book this artist</span>
            </button>
          } />
      </div>
    </div>
  );
}
